import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWebsite } from '../../contexts/WebsiteContext';

const WebsiteSwitcher: React.FC = () => {
  const { websites, selectedWebsiteId, setSelectedWebsiteId, selectedWebsite, isSingleWebsite } = useWebsite();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Hide for single-website users
  if (isSingleWebsite || websites.length === 0) return null;

  const displayName = selectedWebsite
    ? (selectedWebsite.businessName || selectedWebsite.domain)
    : 'All Websites';

  return (
    <div ref={ref} className="border-b border-white/10 px-4 py-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left ring-1 ring-white/15 transition-colors hover:bg-white/5"
      >
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-eyebrow text-zinc-500">Website</p>
          <p className="truncate text-sm font-medium text-white">{displayName}</p>
        </div>
        <motion.svg
          className="h-4 w-4 flex-shrink-0 text-zinc-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1.5 max-h-64 overflow-y-auto rounded-lg border border-zinc-200 bg-white py-1">
              {/* All Websites option */}
              <button
                onClick={() => { setSelectedWebsiteId('all'); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                  selectedWebsiteId === 'all'
                    ? 'bg-paper-deep font-medium text-primary'
                    : 'text-primary hover:bg-paper'
                }`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
                </svg>
                All Websites
              </button>

              {/* Divider */}
              <div className="my-1 border-t border-zinc-100" />

              {/* Individual sites */}
              {websites.map((site) => (
                <button
                  key={site.id}
                  onClick={() => { setSelectedWebsiteId(site.id); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    selectedWebsiteId === site.id
                      ? 'bg-paper-deep font-medium text-primary'
                      : 'text-primary hover:bg-paper'
                  }`}
                >
                  {/* Status dot */}
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    site.status === 'active' ? 'bg-green-500' : site.status === 'setting_up' ? 'bg-amber-500' : 'bg-zinc-400'
                  }`} />
                  <span className="truncate">{site.businessName || site.domain}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WebsiteSwitcher;
